import { useState } from 'react';
import { siteInfo } from '@/mocks/categories';

const FORM_ACTION = 'https://readdy.ai/api/form/dahh6mrp14a1h3mr6mq0';

const interests = ['全部板块', '宿舍夜聊', '课堂八卦', '社团趣事', '食堂奇遇', '校园奇闻'];

type Status = 'idle' | 'success' | 'error';

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // 先按真实 DOM 读取数据，避免浏览器自动填充绕过组件状态
    const formData = new FormData(form);
    const decoy = String(formData.get('website_alt') ?? '').trim();

    // 蜜罐被填写：不发送请求，给出与正常提交一致的反馈
    if (decoy) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setSubmitting(true);
    setStatus('idle');
    setFormError('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isOk = response.ok && parsed?.code === 'OK';
      const looksSpam = /spam/i.test(serverMsg);

      if (isOk && !looksSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络不太顺畅，请检查连接后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="subscribe" className="bg-background-100/60 scroll-mt-20 py-14 md:py-20">
      <div className="mx-auto w-full max-w-content px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-mail-star-line" />
              每日热瓜订阅
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              每个工作日早间，一封不多不少的校园速报
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              我们会挑选当天最值得一读的一到两篇高校吃瓜趣闻，连同最新的校园话题榜一起发送到你的邮箱。
              没有值得推送的内容时，我们宁愿不发。
            </p>
            <ul className="mt-6 space-y-3">
              {['只发真正值得看的故事，拒绝凑数', '可随时回复退订，我们不会追问原因', '投稿邮箱长期开放，欢迎分享你的校园日常'].map((tip) => (
                <li key={tip} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-foreground-500">
              也可以直接投稿：<a href={`mailto:${siteInfo.submitEmail}`} className="text-primary-600 underline-offset-2 hover:underline">{siteInfo.submitEmail}</a>
            </p>
          </div>

          <div className="flex flex-col justify-center">
            <form
              id="guatian-subscribe-form"
              data-readdy-form
              action={FORM_ACTION}
              method="POST"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6"
            >
              <label className="block text-sm font-medium text-foreground-800" htmlFor="subscribe-email">
                你的邮箱地址
              </label>
              <input
                id="subscribe-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="name@example.com"
                className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />

              <label className="mt-4 block text-sm font-medium text-foreground-800" htmlFor="subscribe-interest">
                最想先看哪个板块
              </label>
              <select
                id="subscribe-interest"
                name="interest"
                className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              >
                {interests.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              <input
                type="text"
                name="website_alt"
                className="field-decoy"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? '正在提交…' : '订阅每日热瓜'}
              </button>

              {status === 'success' && (
                <p className="mt-3 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs text-accent-900">
                  <i className="ri-checkbox-circle-line" />
                  订阅成功，下一封热瓜会在工作日早间送达你的邮箱。
                </p>
              )}
              {status === 'error' && (
                <p className="mt-3 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-xs text-primary-800">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}

              <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
                提交即表示同意我们仅将该邮箱用于内容推送。本页面不会收集与订阅无关的个人信息。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}